import React, { createContext, useEffect, useState } from 'react';
import apiClient from '../api/apiClient';
import {
  clearAuthTokens,
  getAccessToken,
  onAuthLogout,
  onAuthTokensUpdated,
  setAuthTokens,
} from '../api/authStorage';

export const AuthContext = createContext();

export function AuthProvider({ children }) {
  const initialToken = getAccessToken();
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(initialToken);
  const [loading, setLoading] = useState(Boolean(initialToken));

  useEffect(() => {
    const unsubscribeTokens = onAuthTokensUpdated(({ access }) => {
      setToken(access || null);
    });

    const unsubscribeLogout = onAuthLogout(() => {
      setToken(null);
      setUser(null);
      setLoading(false);
    });

    return () => {
      unsubscribeTokens();
      unsubscribeLogout();
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);
    apiClient
      .get('/users/me/', { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        setUser(res.data);
      })
      .catch((err) => {
        console.error('Error fetching user profile:', err);
        clearAuthTokens();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const login = async ({ employee_number, password }) => {
    try {
      setLoading(true);
      const res = await apiClient.post('/token/', {
        employee_number,
        password,
      });

      const accessToken = res.data.access;
      const refreshToken = res.data.refresh;

      setAuthTokens({ access: accessToken, refresh: refreshToken });
      setToken(accessToken);

      if (res.data.user) {
        setUser(res.data.user);
        return;
      }

      const profileRes = await apiClient.get('/users/me/', {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      setUser(profileRes.data);
    } catch (err) {
      console.error('Login failed:', err);
      clearAuthTokens();
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    clearAuthTokens();
    setLoading(false);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}
